/**
 * ADR-0069 N20 — the program-type note. Note tier: the neutral `notice`, never the amber or warn
 * chrome, because it is a question for a person and moves no score. Our vocabulary is PROGRAM TYPE;
 * nothing here says "regime".
 *
 * Three shapes (engine `program-type.ts`): the documents carry tax-credit facts but the run read
 * market-rate; the run read tax-credit with no fact behind it; or the record settled it (Brad,
 * 2026-10-06) and the documents still point the other way — one calm line, never a warning.
 *
 * Every signal is shown by its plain-language NAME. The note carries no figure, by construction.
 */

import type { ProgramTypeNoteView } from '../api.js';

/** "a, b and c" — a list a person would say aloud. */
export function plainList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** What the record pins, in words. */
function pinnedLabel(pinned: Extract<ProgramTypeNoteView, { kind: 'settled' }>['pinned']): string {
  return plainList(
    pinned.map((p) =>
      p.field === 'isLIHTC'
        ? p.value === true
          ? 'LIHTC'
          : 'market-rate'
        : `${String(p.value)} financing`,
    ),
  );
}

/** The facts the reverse case looked for and did not find. Mirrors the engine's signal set. */
const LOOKED_FOR =
  'tax-credit equity, credit amounts, eligible basis, credit pricing, a housing finance agency ' +
  'application, AMI rent targeting, HOME funds, soft debt, a deferred developer fee or a stated ' +
  'credit type';

/*
  ⚠️ "the next run", not "this note": a pin applies to the runs after it. This run's note was issued
  with the run and is carried forward unchanged on a republish, so promising that pinning settles THIS
  note would be untrue on the page the reader is looking at.
*/
const HOW_TO_SETTLE =
  'Pin the program type as a deal fact on the project record and the next run uses it, settled.';

/** The note's sentences. Exported for the test; the component only arranges them. */
export function programTypeNoteText(note: ProgramTypeNoteView): { title: string; body: string[] } {
  switch (note.kind) {
    case 'documents-tax-credit':
      return {
        title: 'Program type — worth confirming',
        body: [
          `The documents carry tax-credit facts (${plainList(note.signals.map((s) => s.label))}), but this run read the deal as market-rate, so the market-rate treatment applied: the Value Creation Gate and the conventional loan-to-cost cap.`,
          'If this is a tax-credit deal, the tax-credit treatment applies instead and the score could change. ' +
            HOW_TO_SETTLE,
        ],
      };
    case 'reading-tax-credit-no-signal':
      return {
        title: 'Program type — worth confirming',
        body: [
          `This run read the deal as tax-credit, but we found none of the tax-credit facts we look for in the documents (${LOOKED_FOR}), so the tax-credit treatment rests on the reading alone.`,
          // Not an accusation: a bond- or agency-financed deal reads as tax-credit-subsidized and can
          // be right while stating none of the facts above.
          'Tax-exempt bond or HUD financing on its own is not one of those facts, so a deal financed that way can land here and still be read correctly.',
          'If this is a market-rate deal, the market-rate treatment applies instead and the score could change. ' +
            HOW_TO_SETTLE,
        ],
      };
    case 'settled': {
      const docs =
        note.documents === 'tax-credit-signals'
          ? `The documents show tax-credit facts (${plainList(note.signals.map((s) => s.label))})`
          : 'The documents show none of the tax-credit facts we look for';
      const who = note.recordedByName?.trim() ? ` by ${note.recordedByName.trim()}` : '';
      return {
        title: 'Program type — settled on the record',
        body: [
          `${docs}; the project record settles it as ${pinnedLabel(note.pinned)}, pinned${who} (${note.recordedAt.slice(0, 10)}), reason on the record.`,
        ],
      };
    }
  }
}

export function ProgramTypeNote({ note }: { note: ProgramTypeNoteView }) {
  const { title, body } = programTypeNoteText(note);
  return (
    <section className="notice program program-type-note" aria-label="Program type note">
      <strong>{title}.</strong>
      {body.map((p) => (
        <p className="program__prose" key={p}>
          {p}
        </p>
      ))}
    </section>
  );
}
